import { AgTechQueryContentConfiguration, AgTechQueryResult } from "@agtech/core/data/queries/AgTechDataQueries"
import { IAgTechGridItem, AgTechGridCoreColumnMetadata } from "@agtech/web/components/Grids/Data/AgTechGridData"
import { QueryKey } from "@tanstack/react-query"

export type AgTechImportValidationError = {
    errorMessage: string,
    importedValue: string
}

export type AgTechImportInvalidColumnIndex = {
    [columnName: string]: AgTechImportValidationError
}

export type AgTechImportItem<TItem> = {
    itemRow: number,
    importedItem: TItem,
    isValidItem: boolean,
    isConflictingItem: boolean,
    invalidColumns: AgTechImportInvalidColumnIndex,
    importFailureMessage?: string
}

export const isItemImportValid = <TItem, >(importItem: AgTechImportItem<TItem>) => {
    return importItem.isValidItem && !importItem.isConflictingItem;
}

export type AgTechImportGridItem<TItem> = IAgTechGridItem & {
    importItem: AgTechImportItem<TItem>
}

export type AgTechImportValidationResult = {
    isValid: boolean,
    errorMessage?: string
}

export type AgTechImportColumn<TItem, TContext = {}> = {
    columnName: string,
    getItemValue: (item: TItem, context: TContext) => string,
    validateItemValue?: (importedValue: string, allImportedValues: string[], context: TContext) => AgTechImportValidationResult
    setItemValue: (item: TItem, value: string, context: TContext) => void,
    getItemGridColumn?: () => AgTechGridCoreColumnMetadata
}

export type AgTechImportExecutionProps<TItem, TContext = {}> = {
    columns: AgTechImportColumn<TItem, TContext>[],
    items: AgTechImportItemsProps<TItem, TContext>
}

export type AgTechImportData<TItem, TContext = {}> = {
    items: TItem[],
    context: TContext
};

export type AgTechImportItemsProps<TItem, TContext = {}> = {
    defaultItem: TItem,
    existingItemsQuery: {
        queryKey: QueryKey,
        queryResult: AgTechQueryResult<{
            items: TItem[],
            context?: TContext
        }>,
        refresh: () => Promise<any>,
        content: AgTechQueryContentConfiguration
    },
    itemKeys: ((item: TItem) => { key: string, warning: string })[]
}

export type AgTechImportResult<TItem> = {
    items: AgTechImportItem<TItem>[],
    wasSuccessful: boolean,
    hadConflicts: boolean
}

export type AgTechImportStep = {
    instruction: string,
    details?: string,
    body?: () => React.ReactElement
}


/// Props

export type AgTechImportTerminologyProps = {
    importEntitySingular: string,
    importEntityPlural: string,
}

export type AgTechImportModalResultViewProps<TItem, TContext = {}> = {
    importProps: AgTechImportExecutionProps<TItem, TContext>,
    importResult: AgTechImportResult<TItem>,
    importData: AgTechImportData<TItem, TContext>,
    content: AgTechImportTerminologyProps,
    onImportAgain: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
}